import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Button,
  TablePagination,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import moment from 'moment';
import { getRdv } from '../services/api';
import MapSatelliteModal from '../components/MapSatelliteModal';
import { useTranslation } from 'react-i18next';

const RdvList = () => {
  const { t } = useTranslation();
  
  // États des filtres et données
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterEquipe, setFilterEquipe] = useState('');
  const [filterCp, setFilterCp] = useState('');
  const [filterNom, setFilterNom] = useState('');
  const [filterOrigine, setFilterOrigine] = useState('');
  const [filterFiliale, setFilterFiliale] = useState('');
  const [rdvs, setRdvs] = useState([]);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Options pour les picklists Origine et Filiale
  const [origineOptions, setOrigineOptions] = useState([]);
  const [filialeOptions, setFilialeOptions] = useState([]);
  
  // États pour la pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Récupération des RDV depuis l'API
  useEffect(() => {
    const fetchRdvs = async () => {
      const data = await getRdv();
      setRdvs(data);
      
      // Extraire et trier alphabétiquement pour Origine et Filiale
      const origines = [...new Set(data.map(rdv => rdv.Origine).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
      const filiales = [...new Set(data.map(rdv => rdv.filiale).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
      
      setOrigineOptions(origines);
      setFilialeOptions(filiales);
    };
    fetchRdvs();
  }, []);

  // Filtrer les RDV selon les critères
  useEffect(() => {
    const filtered = rdvs.filter(rdv => {
      const matchDate = filterDate ? rdv.rdv_date === filterDate : true;
      const matchEquipe = filterEquipe ? (rdv.equipe || '').toLowerCase() === filterEquipe.toLowerCase() : true;
      const matchCp = filterCp ? rdv.cp.includes(filterCp) : true;
      const fullName = `${rdv.Nom || ''} ${rdv.Prenom || ''}`.toLowerCase();
      const matchNom = filterNom ? fullName.includes(filterNom.toLowerCase()) : true;
      const matchOrigine = filterOrigine ? rdv.Origine === filterOrigine : true;
      const matchFiliale = filterFiliale ? rdv.filiale === filterFiliale : true;
      return matchDate && matchEquipe && matchCp && matchNom && matchOrigine && matchFiliale;
    });
    setFilteredRdvs(filtered);
  }, [rdvs, filterDate, filterEquipe, filterCp, filterNom, filterOrigine, filterFiliale]);

  // État local pour les RDV filtrés
  const [filteredRdvs, setFilteredRdvs] = useState([]);

  // Pagination
  const paginatedRdvs = filteredRdvs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (rdv) => {
    setSelectedRdv(rdv);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRdv(null);
  };

  const resetFilters = () => {
    setFilterDate(new Date().toISOString().split('T')[0]);
    setFilterEquipe('');
    setFilterCp('');
    setFilterNom('');
    setFilterOrigine('');
    setFilterFiliale('');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Titre de la page */}
      <Typography variant="h4" gutterBottom>
        {t('list.rdvTitle')}
      </Typography>
      
      {/* Nombre de résultats */}
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {t('list.numberOfResults', { count: filteredRdvs.length })}
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label={t('filters.date')}
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="equipe-select-label">{t('filters.team')}</InputLabel>
            <Select
              labelId="equipe-select-label"
              id="equipe-select"
              value={filterEquipe}
              label={t('filters.team')}
              onChange={(e) => setFilterEquipe(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('filters.team')}</em>
              </MenuItem>
              {Array.from(new Set(rdvs.map(rdv => rdv.equipe).filter(Boolean)))
                .sort((a, b) => a.localeCompare(b))
                .map(equipe => (
                  <MenuItem key={equipe} value={equipe}>
                    {equipe}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('filters.department')}
            value={filterCp}
            onChange={(e) => setFilterCp(e.target.value)}
            size="small"
          />
          <TextField
            label={t('filters.name')}
            value={filterNom}
            onChange={(e) => setFilterNom(e.target.value)}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="origine-select-label">{t('filters.origin')}</InputLabel>
            <Select
              labelId="origine-select-label"
              id="origine-select"
              value={filterOrigine}
              label={t('filters.origin')}
              onChange={(e) => setFilterOrigine(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('filters.origin')}</em>
              </MenuItem>
              {origineOptions.map((origine) => (
                <MenuItem key={origine} value={origine}>
                  {origine}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="filiale-select-label">{t('filters.branch')}</InputLabel>
            <Select
              labelId="filiale-select-label"
              id="filiale-select"
              value={filterFiliale}
              label={t('filters.branch')}
              onChange={(e) => setFilterFiliale(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('filters.branch')}</em>
              </MenuItem>
              {filialeOptions.map((filiale) => (
                <MenuItem key={filiale} value={filiale}>
                  {filiale}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={resetFilters}>
            {t('filters.reset')}
          </Button>
        </Box>
      </Paper>
      
      {/* Tableau des RDV */}
      <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('table.column.date')}</TableCell>
              <TableCell>{t('table.column.time')}</TableCell>
              <TableCell>{t('table.column.client')}</TableCell>
              <TableCell>{t('table.column.branch')}</TableCell>
              <TableCell>{t('table.column.origin')}</TableCell>
              <TableCell>{t('table.column.status')}</TableCell>
              <TableCell>{t('table.column.estimatedSurface')}</TableCell>
              <TableCell>{t('table.column.installedSurface')}</TableCell>
              <TableCell>{t('table.column.installationType')}</TableCell>
              <TableCell>{t('table.column.team')}</TableCell>
              <TableCell>{t('table.column.postalCode')}</TableCell>
              <TableCell>{t('table.column.address')}</TableCell>
              <TableCell>{t('table.column.action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRdvs.map((rdv, index) => (
              <TableRow key={index}>
                <TableCell>{moment(rdv.rdv_date).format('DD/MM/YYYY')}</TableCell>
                <TableCell>{rdv.rdv_time}</TableCell>
                <TableCell>
                  {rdv.Nom && rdv.Prenom 
                    ? `${rdv.Nom} ${rdv.Prenom}`
                    : rdv.Nom || rdv.Prenom || "-"}
                </TableCell>
                <TableCell>{rdv.filiale || "-"}</TableCell>
                <TableCell>{rdv.Origine || "-"}</TableCell>
                <TableCell>{rdv.status || "-"}</TableCell>
                <TableCell>{rdv.surface_estime || "-"}</TableCell>
                <TableCell>{rdv.surface_installe || "-"}</TableCell>
                <TableCell>{rdv.type_install || "-"}</TableCell>
                <TableCell>{rdv.equipe || "-"}</TableCell>
                <TableCell>{rdv.cp}</TableCell>
                <TableCell>{rdv.address}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button variant="contained" onClick={() => handleOpenModal(rdv)}>
                      {t('list.viewSatellite', { defaultValue: "Vue Satellite" })}
                    </Button>
                    {rdv.leadid && (
                      <Button
                        variant="contained"
                        color="primary"
                        href={`https://kortex.phone.gs/vtigercrm/index.php?module=Leads&view=Detail&record=${rdv.leadid}&app=MARKETING`}
                        target="_blank"
                      >
                        {t('rdvDetails.accessFile')}
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredRdvs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>
      
      {selectedRdv && (
        <MapSatelliteModal
          open={modalOpen}
          handleClose={() => {
            setModalOpen(false);
            setSelectedRdv(null);
          }}
          address={selectedRdv.address}
        />
      )}
    </Box>
  );
};

export default RdvList;
